import {
  CategorizedBranchData,
  CategorizedList,
  CategorizedTipData,
} from '~/shared/components/ui/CategorizedCheckList';
import { CheckItem } from '~/shared/components/ui/CategorizedCheckList/util';
import { mtechnavi } from '~/shared/libs/clientsdk';
import { DisplayNameLang } from '~/shared/utils/commonType';
import { MultipleType } from './util';

/**
 * 企業属性データの型情報。
 * API を介さず json で取得するためここで定義しておく。
 */
export interface BusinessUnitAttributes {
  children: BusinessUnitAttributeCategory[];
}
export interface BusinessUnitAttributeCategory {
  attributeCategoryId?: string;
  displayNameLang?: DisplayNameLang;
  children?: BusinessUnitAttributeCategory[] | BusinessUnitAttributeItem[];
}
export interface BusinessUnitAttributeItem {
  attributeItemId?: string;
  displayNameLang?: DisplayNameLang;
}

/**
 * 企業属性の形式である json データを CategorizedCheckList で扱うデータに変換する。
 * カテゴリに識別可能な ID を割り振る。
 */
interface CategoryData {
  attributeCategoryId?: string;
  displayNameLang?: DisplayNameLang;
  children?: CategoryData[];
}
interface TipData {
  attributeItemId?: string;
  displayNameLang?: DisplayNameLang;
}

export const convertToCategorizedList = (items: unknown): CategorizedList => {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map((item: unknown, index) => {
    const id = `category-${index}`;
    if (!isCategoryData(item)) {
      return {
        CategorizedItemId: id,
        children: [],
      };
    }
    return {
      CategorizedItemId: item.attributeCategoryId || id,
      displayNameLang: item.displayNameLang,
      children: convertToCategorizedListChildren(item.children || [], id),
    };
  });
};
const convertToCategorizedListChildren = (
  items: unknown,
  parentId: string
): CategorizedBranchData[] | CategorizedTipData[] => {
  if (!Array.isArray(items)) {
    return [];
  }
  if (isCategoryDataList(items)) {
    return items.map((item, index): CategorizedBranchData => {
      const id = `${parentId}-${index}`;
      return {
        CategorizedItemId: item.attributeCategoryId || id,
        displayNameLang: item.displayNameLang,
        children: convertToCategorizedListChildren(item?.children || [], id),
      };
    });
  } else {
    return items.map((item: unknown, index): CategorizedTipData => {
      const id = `${parentId}-${index}`;
      if (!isTipData(item)) {
        return {
          CategorizedItemId: id,
        };
      }
      return {
        CategorizedItemId: item.attributeItemId || id,
        displayNameLang: item.displayNameLang,
      };
    });
  }
};
const isCategoryData = (item: unknown): item is CategoryData => {
  return Object.hasOwn(item as Object, 'attributeCategoryId');
};
const isTipData = (item: unknown): item is TipData => {
  return Object.hasOwn(item as Object, 'attributeItemId');
};
const isCategoryDataList = (list: unknown[]): list is CategoryData[] => {
  return list.every((item) =>
    Object.hasOwn(item as CategoryData | TipData, 'children')
  );
};

export const convertMultipleTypeToFilterType = (
  multipleType: MultipleType
): mtechnavi.api.analysis.ListCompanysRequest.DataFilter.FilterType => {
  switch (multipleType) {
    case 'or':
      return mtechnavi.api.analysis.ListCompanysRequest.DataFilter.FilterType
        .OR;
    case 'and':
      return mtechnavi.api.analysis.ListCompanysRequest.DataFilter.FilterType
        .AND;
    default:
      throw new Error(`unknown multipleType ${multipleType}`);
  }
};
export const convertFilterTypeToMultipleType = (
  filterType: mtechnavi.api.analysis.ListCompanysRequest.DataFilter.FilterType
): MultipleType => {
  switch (filterType) {
    case mtechnavi.api.analysis.ListCompanysRequest.DataFilter.FilterType.OR:
      return 'or';
    case mtechnavi.api.analysis.ListCompanysRequest.DataFilter.FilterType.AND:
      return 'and';
    default:
      throw new Error(`unknown multipleType ${filterType}`);
  }
};

/* 材料・組織のデータをフラットに詰め替える*/

interface FlatNode {
  node: BusinessUnitAttributeCategory | BusinessUnitAttributeItem;
  path: BusinessUnitAttributeCategory[];
  rootType: string;
}

export interface MaterialSkillRelationItem {
  materialCategoryName: string; // 最上位階層
  /** 集約処理3: 材料第一階層が同一の塊の行数 */
  materialSpan: number;
  isMaterialFirst: boolean;

  categoryName: string; // 材料第二階層
  categorySpan: number;
  isCategoryFirst: boolean;

  materialCheckItems: CheckItem[];
  materialNames: string; // 材料末端（集約済）
  /** 仕様上は rowSpan しないため常に 1 */
  materialAggregateSpan: number;
  isMaterialAggregateFirst: boolean;

  skillCheckItems: CheckItem[];
  skillNames: string; // 技術末端（集約済）
  /** 集約処理3: 技術列の rowSpan（materialSpan と同じ塊） */
  skillSpan: number;
  isSkillFirst: boolean;
  /** 技術編集時に使用する、同一 skillSpan 範囲の材料末端集合 */
  skillTargetMaterialCheckItems: CheckItem[];
}

// --- 型ガード (any排除の肝) ---
function isBusinessUnitAttributeItem(
  node: BusinessUnitAttributeCategory | BusinessUnitAttributeItem
): node is BusinessUnitAttributeItem {
  return (node as BusinessUnitAttributeItem).attributeItemId !== undefined;
}

/**
 * 再帰的に走査してMapを作成する
 */
function createObjectMap(
  master: BusinessUnitAttributes
): Map<string, FlatNode> {
  const masterMap = new Map<string, FlatNode>();

  const traverse = (
    nodes: (BusinessUnitAttributeCategory | BusinessUnitAttributeItem)[],
    currentPath: BusinessUnitAttributeCategory[],
    rootType: string
  ) => {
    for (const node of nodes) {
      if (isBusinessUnitAttributeItem(node)) {
        // BusinessUnitAttributeItem の場合
        masterMap.set(node.attributeItemId ?? '', {
          node,
          path: [...currentPath],
          rootType,
        });
      } else {
        // BusinessUnitAttributeCategory の場合
        masterMap.set(node.attributeCategoryId ?? '', {
          node,
          path: [...currentPath],
          rootType,
        });
        if (node.children) {
          traverse(node.children, [...currentPath, node], rootType);
        }
      }
    }
  };

  master.children.forEach((root) =>
    traverse(root.children ?? [], [], root.attributeCategoryId ?? '')
  );
  return masterMap;
}

const KEY_SEP = '\u0001';

function sortedJoinedIds(items: CheckItem[]): string {
  return items
    .map((it) => it.id ?? '')
    .filter(Boolean)
    .sort()
    .join(KEY_SEP);
}

/** 集約処理2 後の論理行 */
interface Agg2Row {
  materialCategoryName: string;
  categoryName: string;
  materialCheckItems: CheckItem[];
  materialNames: string;
  skillCheckItems: CheckItem[];
  skillNames: string;
}

/**
 * 表示用の行データを生成する
 *
 * 集約1: 材料第一・第二・材料末端が同一 → 技術末端を配列化
 * 集約2: 材料第一・第二・技術末端(集約済) が同一 → 材料末端を配列化
 * ORDER BY: 第一階層, 技術末端表示, 第二階層, 材料末端表示
 * 集約3(表示): 材料第一階層が同一の塊で第一階層セルを rowSpan。
 * 同一材料第一階層内で連続する同一技術末端（集約済）は技術セルを rowSpan。
 * 材料第二階層・材料末端（集約済）は rowSpan しない。
 */
export function generateMaterialSkillRelationItems(
  master: BusinessUnitAttributes | undefined,
  relations: mtechnavi.api.company.ISkillRelation[]
): MaterialSkillRelationItem[] {
  if (!master) return [];
  const masterMap = createObjectMap(master);

  // 集約1: (第一階層, 第二階層, 材料末端) -> 技術末端配列
  const agg1 = new Map<
    string,
    {
      materialCategoryName: string;
      categoryName: string;
      materialCheckItem: CheckItem;
      skillById: Map<string, CheckItem>;
    }
  >();

  for (const rel of relations) {
    const mItem = masterMap.get(rel.materialItemId ?? '');
    const sItem = masterMap.get(rel.technologyItemId ?? '');
    const materialCategoryName = mItem?.path[0]?.displayNameLang?.ja || '';
    const categoryName = mItem?.path[1]?.displayNameLang?.ja || '';
    const technologyItemId = rel.technologyItemId ?? '';
    const mName = mItem?.node.displayNameLang?.ja ?? '';
    const sName = sItem?.node.displayNameLang?.ja ?? '';

    const agg1Key = [
      materialCategoryName,
      categoryName,
      rel.materialItemId ?? '',
    ].join(KEY_SEP);
    let bucket = agg1.get(agg1Key);
    if (!bucket) {
      bucket = {
        materialCategoryName,
        categoryName,
        materialCheckItem: {
          id: rel.materialItemId ?? '',
          displayNameLang: { ja: mName },
        },
        skillById: new Map(),
      };
      agg1.set(agg1Key, bucket);
    }
    if (sName && technologyItemId) {
      bucket.skillById.set(technologyItemId, {
        id: technologyItemId,
        displayNameLang: { ja: sName },
      });
    }
  }

  // 集約2: (第一階層, 第二階層, 技術末端集合) -> 材料末端配列
  const agg2Map = new Map<string, Agg2Row>();

  for (const bucket of Array.from(agg1.values())) {
    const skillCheckItems = Array.from(bucket.skillById.values()).sort((a, b) =>
      (a.id ?? '').localeCompare(b.id ?? '', 'ja')
    );
    const skillIds = sortedJoinedIds(skillCheckItems);
    const agg2Key = [
      bucket.materialCategoryName,
      bucket.categoryName,
      skillIds,
    ].join(KEY_SEP);

    if (!agg2Map.has(agg2Key)) {
      agg2Map.set(agg2Key, {
        materialCategoryName: bucket.materialCategoryName,
        categoryName: bucket.categoryName,
        materialCheckItems: [],
        materialNames: '',
        skillCheckItems,
        skillNames: skillCheckItems
          .map((s) => s.displayNameLang?.ja ?? '')
          .filter(Boolean)
          .sort((x, y) => x.localeCompare(y, 'ja'))
          .join('、 '),
      });
    }
    const row = agg2Map.get(agg2Key)!;
    const mid = bucket.materialCheckItem.id ?? '';
    const existing = row.materialCheckItems.find((m) => (m.id ?? '') === mid);
    if (!existing && mid) {
      row.materialCheckItems.push(bucket.materialCheckItem);
    }
  }

  const sortedAgg2: Agg2Row[] = Array.from(agg2Map.values());
  for (const row of sortedAgg2) {
    row.materialCheckItems.sort((a, b) =>
      (a.displayNameLang?.ja ?? '').localeCompare(
        b.displayNameLang?.ja ?? '',
        'ja'
      )
    );
    row.materialNames = row.materialCheckItems
      .map((m) => m.displayNameLang?.ja ?? '')
      .filter(Boolean)
      .join('、 ');
    row.skillCheckItems.sort((a, b) =>
      (a.displayNameLang?.ja ?? '').localeCompare(
        b.displayNameLang?.ja ?? '',
        'ja'
      )
    );
    row.skillNames = row.skillCheckItems
      .map((s) => s.displayNameLang?.ja ?? '')
      .filter(Boolean)
      .join('、 ');
  }
  sortedAgg2.sort((a, b) => {
    if (a.materialCategoryName !== b.materialCategoryName) {
      return a.materialCategoryName.localeCompare(b.materialCategoryName, 'ja');
    }
    if (a.skillNames !== b.skillNames) {
      return a.skillNames.localeCompare(b.skillNames, 'ja');
    }
    if (a.categoryName !== b.categoryName) {
      return a.categoryName.localeCompare(b.categoryName, 'ja');
    }
    return a.materialNames.localeCompare(b.materialNames, 'ja');
  });

  const rows: MaterialSkillRelationItem[] = [];
  sortedAgg2.forEach((item, i) => {
    const materialSpan = sortedAgg2.filter(
      (r) => r.materialCategoryName === item.materialCategoryName
    ).length;
    const skillSpan = sortedAgg2.filter(
      (r) =>
        r.materialCategoryName === item.materialCategoryName &&
        r.skillNames === item.skillNames
    ).length;
    const isMaterialFirst =
      i === 0 ||
      sortedAgg2[i - 1].materialCategoryName !== item.materialCategoryName;
    const isSkillFirst =
      i === 0 ||
      sortedAgg2[i - 1].materialCategoryName !== item.materialCategoryName ||
      sortedAgg2[i - 1].skillNames !== item.skillNames;
    const skillBlockRows = sortedAgg2.filter(
      (r) =>
        r.materialCategoryName === item.materialCategoryName &&
        r.skillNames === item.skillNames
    );
    const skillTargetMaterialCheckItems = skillBlockRows
      .flatMap((r) => r.materialCheckItems)
      .filter(
        (m, idx, arr) =>
          idx === arr.findIndex((x) => (x.id ?? '') === (m.id ?? ''))
      );

    rows.push({
      materialCategoryName: item.materialCategoryName,
      materialSpan,
      isMaterialFirst,

      categoryName: item.categoryName,
      categorySpan: 1,
      isCategoryFirst: true,

      materialCheckItems: item.materialCheckItems,
      materialNames: item.materialNames,
      materialAggregateSpan: 1,
      isMaterialAggregateFirst: true,

      skillCheckItems: item.skillCheckItems,
      skillNames: item.skillNames,
      skillSpan,
      isSkillFirst,
      skillTargetMaterialCheckItems,
    });
  });

  return rows;
}
